import {
  createFileRoute,
  useParams,
  Outlet,
  useLocation,
} from "@tanstack/react-router"
import { checkPermissions } from "@/utils/checkPermissions"
import {
  LayoutWithSidebar,
  DetailSidebar,
  useLayoutConfig,
  ProfileCard,
  Separator,
  NavigationItem,
} from "toptaal-shared"
import ScreenWrapper from "@/components/ScreenWrapper"
import { useOrganizationUserByIdSuspenseQuery } from "@/api/queries/organizationusers/useOrganizationUserByIdSuspenseQuery"
import { useOrganizationQuery } from "@/api/queries/organizations/useOrganizationQuery"
import { useOrganizationCompanyPictureQuery } from "@/api/queries/organizations/useOrganizationCompanyPictureQuery"

export const Route = createFileRoute(
  "/beheer/contactpersonen/$organizationUserId",
)({
  component: Parent,
  beforeLoad: () => checkPermissions("read", "contacts"),
})

function Parent() {
  const { pathname } = useLocation()
  const layoutConfig = useLayoutConfig()

  const { organizationUserId } = useParams({
    from: "/beheer/contactpersonen/$organizationUserId",
  })

  const base = `/beheer/contactpersonen/${organizationUserId}`
  const sidebarNavigationItems = [
    {
      label: "Gegevens",
      to: `${base}/gegevens`,
    },
    {
      label: "Gekoppelde cursisten",
      to: `${base}/gekoppelde-cursisten`,
    },
  ]

  return (
    <ScreenWrapper className="!px-0">
      <LayoutWithSidebar
        rightPanelClassName={layoutConfig.rightPanelClassName}
        disableRightPanelSuspense={layoutConfig.disableRightPanelSuspense}
        sidebar={
          <OrganizationUserSidebar
            organizationUserId={Number(organizationUserId)}
            sidebarNavigationItems={sidebarNavigationItems}
            isItemActive={(route: string) => pathname.startsWith(route)}
          />
        }
      >
        <Outlet />
      </LayoutWithSidebar>
    </ScreenWrapper>
  )
}

function OrganizationUserSidebar({
  organizationUserId,
  sidebarNavigationItems,
  isItemActive,
}: {
  organizationUserId: number
  sidebarNavigationItems: NavigationItem[]
  isItemActive: (route: string) => boolean
}) {
  const { data: organizationUser } = useOrganizationUserByIdSuspenseQuery({
    organizationUserId,
  })

  const { data: organization } = useOrganizationQuery({
    organizationId: organizationUser.organizationId,
    enabled: !!organizationUser,
  })

  const { data: organizationStyle } = useOrganizationCompanyPictureQuery({
    organizationId: organizationUser.organizationId ?? 0,
    hasCompanyPicture:
      !!organization?.style.hasCompanyPicture &&
      !!organizationUser.organizationId,
  })

  return (
    <DetailSidebar
      items={sidebarNavigationItems}
      isItemActive={isItemActive}
      goBackLabel="Contactpersoon"
      goBackRoute="/beheer/contactpersonen"
    >
      <ProfileCard
        firstName={organizationUser.firstName}
        lastName={organizationUser.lastName}
        email={organizationUser.email}
        phoneNumber={organizationUser.phoneNumber}
      />

      <Separator className="my-6" />

      <h3 className="mb-2">Organisatie</h3>

      <ProfileCard
        firstName={organization?.name}
        lastName=""
        email={organization?.email}
        phoneNumber={organization?.phoneNumber}
        imageUrl={organizationStyle?.url}
      />

      <Separator className="my-6" />
    </DetailSidebar>
  )
}
